import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Flask, 
  TestTube, 
  CheckCircle, 
  Clock, 
  Eye,
  ArrowRight,
  ListBullets,
  Warning
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

export default function LabAssistantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [assignedSamples, setAssignedSamples] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      // Fetch stats specific to lab assistant
      const statsRes = await axios.get(`${API}/dashboard/lab-assistant-stats`, {
        params: { userId: user?.id, userName: user?.name }
      });
      setStats(statsRes.data.stats);

      // Fetch samples assigned to this lab assistant
      const samplesRes = await axios.get(`${API}/product-folders`, {
        params: { userId: user?.id, role: user?.role }
      });
      setAssignedSamples(samplesRes.data.folders || []);
    } catch (error) {
      console.error("Lab Assistant Dashboard fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "complete":
      case "finish":
        return "bg-green-100 text-green-700";
      case "started":
        return "bg-blue-100 text-blue-700";
      case "end":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      default:
        return "bg-green-100 text-green-700";
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="animate-pulse text-gray-500 font-poppins">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gradient-to-br from-slate-50 to-vcc-pink-50/30 min-h-full" data-testid="lab-assistant-dashboard">
      {/* Welcome Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-vcc-teal to-vcc-teal/70 rounded-xl flex items-center justify-center shadow-lg">
            <Flask size={24} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 font-poppins">Lab Assistant Dashboard</h1>
            <p className="text-slate-500 font-poppins">Welcome, {user?.name}</p>
          </div>
        </div>
      </motion.div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
          <Card className="bg-white border-l-4 border-l-blue-500 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">{stats?.assignedSamples || 0}</p>
                  <p className="text-sm text-slate-500 font-poppins">Assigned Samples</p>
                </div>
                <Flask size={32} className="text-blue-500" weight="duotone" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
          <Card className="bg-white border-l-4 border-l-yellow-500 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">{stats?.pendingTests || 0}</p>
                  <p className="text-sm text-slate-500 font-poppins">Pending Tests</p>
                </div>
                <Clock size={32} className="text-yellow-500" weight="duotone" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
          <Card className="bg-white border-l-4 border-l-purple-500 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">{stats?.inProgressTests || 0}</p>
                  <p className="text-sm text-slate-500 font-poppins">In Progress</p>
                </div>
                <TestTube size={32} className="text-purple-500" weight="duotone" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4 }}>
          <Card className="bg-white border-l-4 border-l-green-500 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-slate-800">{stats?.completedTests || 0}</p>
                  <p className="text-sm text-slate-500 font-poppins">Completed</p>
                </div>
                <CheckCircle size={32} className="text-green-500" weight="duotone" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Assigned Samples List */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="bg-white shadow-lg">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListBullets size={20} className="text-vcc-teal" weight="bold" />
                <CardTitle className="text-lg font-poppins text-slate-800">My Assigned Samples</CardTitle>
              </div>
              <Badge className="bg-vcc-teal/10 text-vcc-teal font-poppins">
                {assignedSamples.length} Total
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {assignedSamples.length === 0 ? (
              <div className="p-8 text-center">
                <Flask size={48} className="text-slate-300 mx-auto mb-3" weight="duotone" />
                <p className="text-slate-500 font-poppins">No samples assigned to you yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {assignedSamples.map((sample, index) => (
                  <motion.div 
                    key={sample.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-slate-800 font-poppins">{sample.sampleNo}</span>
                          <Badge className={getStatusColor(sample.testStatus)}>
                            {sample.testStatus || "Not Started"}
                          </Badge>
                          {sample.testPriority && (
                            <Badge className={getPriorityColor(sample.testPriority)}>
                              {sample.testPriority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 font-poppins">{sample.productName}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                          <span>Client: {sample.clientName || "N/A"}</span>
                          <span>Iterations: {sample.iterations?.length || 0}</span>
                          {sample.zohoSyncStatus === "Synced" && (
                            <span className="text-green-600">✓ Synced</span>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/products/${sample.id}`)}
                        className="border-vcc-teal text-vcc-teal hover:bg-vcc-teal hover:text-white font-poppins"
                        data-testid={`view-sample-${sample.sampleNo}`}
                      >
                        <Eye size={16} className="mr-1" />
                        View
                        <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-6"
      >
        <Card className="bg-gradient-to-r from-vcc-teal/5 to-vcc-pink-50 border-vcc-teal/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Warning size={20} className="text-amber-500" weight="fill" />
                <span className="text-sm text-slate-600 font-poppins">
                  {stats?.pendingTests > 0 
                    ? `You have ${stats.pendingTests} pending test(s) requiring attention` 
                    : "All tests are up to date!"}
                </span>
              </div>
              {stats?.pendingTests > 0 && (
                <Button
                  size="sm"
                  onClick={() => navigate("/products")}
                  className="bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
                >
                  View All Products
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
